import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AuthModal from '@/components/AuthModal';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, ShoppingCart, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const Products = () => {
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sizeFilter, setSizeFilter] = useState('all');
  const [designFilter, setDesignFilter] = useState('all');
  const [sortBy, setSortBy] = useState('size');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, sizeFilter, designFilter, sortBy]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Apply filters
    if (sizeFilter !== 'all') {
      filtered = filtered.filter(p => p.size === parseInt(sizeFilter));
    }
    if (designFilter !== 'all') {
      filtered = filtered.filter(p => p.design === designFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'size') return a.size - b.size;
      if (sortBy === 'price-asc') return a.price_per_case - b.price_per_case;
      if (sortBy === 'price-desc') return b.price_per_case - a.price_per_case;
      return 0;
    });

    setFilteredProducts(filtered);
  };

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.info('Please login to add items to cart');
      setShowAuthModal(true);
      return;
    }

    try {
      const cartResponse = await axios.get(`${API}/cart`);
      const currentCart = cartResponse.data;
      
      const existingItem = currentCart.items?.find(item => item.product_id === productId);
      const newItems = existingItem
        ? currentCart.items.map(item => 
            item.product_id === productId 
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...(currentCart.items || []), { product_id: productId, quantity: 1 }];

      await axios.post(`${API}/cart`, { 
        user_email: user.email,
        items: newItems 
      });
      toast.success('Added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading products...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Product Catalog</h1>
          <p className="text-lg text-gray-600">Browse our complete collection of premium hot cups</p>
        </div>

        {/* Filters and Sorting */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-testid="filter-section">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Size</label>
              <Select value={sizeFilter} onValueChange={setSizeFilter}>
                <SelectTrigger data-testid="size-filter">
                  <SelectValue placeholder="All Sizes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sizes</SelectItem>
                  <SelectItem value="8">8 oz</SelectItem>
                  <SelectItem value="10">10 oz</SelectItem>
                  <SelectItem value="12">12 oz</SelectItem>
                  <SelectItem value="16">16 oz</SelectItem>
                  <SelectItem value="20">20 oz</SelectItem>
                  <SelectItem value="24">24 oz</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Design</label>
              <Select value={designFilter} onValueChange={setDesignFilter}>
                <SelectTrigger data-testid="design-filter">
                  <SelectValue placeholder="All Designs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Designs</SelectItem>
                  <SelectItem value="White">White</SelectItem>
                  <SelectItem value="POS Cups™">POS Cups&trade;</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <ArrowUpDown className="inline h-4 w-4 mr-1" />
                Sort By
              </label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="size">Size (Small to Large)</SelectItem>
                  <SelectItem value="price-asc">Price (Low to High)</SelectItem>
                  <SelectItem value="price-desc">Price (High to Low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Tiered Pricing Discounts</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="bg-white rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">1-10 Cases</p>
              <p className="text-gray-600">Standard Price</p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-blue-400">
              <p className="text-2xl font-bold text-blue-600">11-50 Cases</p>
              <p className="text-blue-700 font-semibold">Save 5%</p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-blue-600">
              <p className="text-2xl font-bold text-blue-600">51+ Cases</p>
              <p className="text-blue-700 font-semibold">Save 10%</p>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="product-grid" data-testid="products-grid">
          {filteredProducts.map(product => (
            <div 
              key={product.id} 
              className="card p-6 cursor-pointer group"
              onClick={() => navigate(`/products/${product.id}`)}
              data-testid={`product-card-${product.id}`}
            >
              <div className="bg-white rounded-lg h-48 mb-4 flex items-center justify-center overflow-hidden">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                  />
                ) : (
                  <Package className="h-20 w-20 text-blue-600" />
                )}
              </div>
              
              <div className="mb-2">
                <span className={`badge ${product.design === 'POS Cups™' ? 'badge-blue' : 'badge-gray'}`}>
                  {product.design === 'POS Cups™' ? 'POS Cups\u2122' : product.design}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{product.case_quantity} cups per case</p>
              
              <div className="mb-4">
                <p className="text-3xl font-bold text-blue-600">${product.price_per_case.toFixed(2)}</p>
                <p className="text-sm text-gray-500">per case</p>
              </div>
              
              <div className="space-y-2 mb-4 text-sm text-gray-600">
                <p><strong>UPC:</strong> {product.upc}</p>
                <p><strong>Pallet:</strong> {product.cases_per_pallet} cases</p>
              </div>
              
              <Button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(product.id);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Add to Cart
              </Button>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No products found matching your filters</p>
          </div>
        )}
      </div>

      <Footer />
      
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      )}
    </div>
  );
};

export default Products;